/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { isCloudinaryUrl, transformCloudinaryUrl } from '@/lib/cloudinary-image-transform';
import { toImageUrl } from './dynamic-image';
import type { Image } from './dynamic-image';
import type { ImageContext } from '@/lib/cloudinary-image-transform';
import type { AppConfig } from '@/types/config';

export type { ImageContext } from '@/lib/cloudinary-image-transform';

export interface ResolveImageUrlParams {
    /** Direct URL string */
    src?: string;
    /** Image object with disBaseLink/link fields */
    image?: Image;
    /** App config (required for DIS fallback) */
    config?: AppConfig;
    /** Image context — determines Cloudinary transformation. When omitted, falls back to DIS/toImageUrl. */
    context?: ImageContext;
}

/**
 * Unified image URL resolver. Routes Cloudinary URLs through context-driven
 * transformation and non-Cloudinary URLs through DIS logic.
 *
 * @returns Transformed URL string, or empty string if no URL available
 */
export function resolveImageUrl({ src, image, config, context }: ResolveImageUrlParams): string {
    const url = src || image?.disBaseLink || image?.link || '';

    if (!url) {
        return '';
    }

    // Route Cloudinary URLs through context-driven transformation
    if (context && isCloudinaryUrl(url)) {
        return transformCloudinaryUrl(url, context);
    }

    // Fallback to DIS logic for SFCC-hosted images
    return toImageUrl({ src: url, image, config }) || url;
}
